import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Modal} from 'react-bootstrap';
import moment from 'moment';
import SubPageNavigation from '../SubPageNavigation'
import ManagedResourceList from '../shared/ManagedResourceList';
import PageHeader from '../PageHeader';
import SnippetEditor from './SnippetEditor';
import Filters from "../Filters/Filters";
import {pluralize} from "utils/strings";
import useBulkActions from "../../hooks/useBulkActions";
import BulkActions from "../ContactList/BulkActions/BulkActions";
import Edit from "../ContactList/BulkActions/Edit";
import TagList from "../Tagging/TagList";
import {addableTags, removableTags, useTagFilter} from "../../utils/tags";
import {BulkTagAction, BulkUntagAction} from "../Tagging/BulkTagActions";

import './styles/snippet_list.scss';


const SnippetList = (props) => {
  const {
    collectionButtons,
    columns: defaultColumns,
    destroy,
    displayName,
    editModal: EditModal,
    editModalProps,
    filters,
    items: unfilteredItems,
    loadAll,
    load,
    save,
    setFilters,
    tags,
    type,
    links,
    children,

    // withTagging
    addTags,
    reloadTags,
    removeTags,

    // withSorting
    onSort,
    sort,
  } = props;

  const [editSnippet, setEditSnippet] = useState(null);
  const [items, filterFields] = useTagFilter(unfilteredItems, {filters, tags});
  if (tags){
    filterFields.push({
      solr_prompt: 'name',
      display_name: 'Filter by Name:',
      filter_type: 'TextFilter',
    })
  }

  const collectionButtonsProp = (typeof collectionButtons !== 'undefined') ? {collectionButtons: collectionButtons} : [];
  const editModel = items.find(
    item => (item.id === editSnippet)
  ) || {};

  const copyModel = () => {
    const item = items.find(
      item => (item.id === editSnippet)
    ) || {};
    const copiedItem = {};
    Object.assign(copiedItem, item);
    if (copiedItem) {
      copiedItem.name = null;
      copiedItem.id = null;
      copiedItem.updated_at = null;
      copiedItem.created_at = null;
      copiedItem.canonical_name = null;
    }
    return copiedItem;
  };

  const ready = !!editModel;

  const {
    itemIsSelected,
    promptSelectAll,
    selectionMode,
    togglePageTracked,
    toggleTrackedItem,
    isNoneSelected
  } = useBulkActions(items);

  const columns = [...defaultColumns];
  const showTagging = !!(tags && unfilteredItems.find(item => !item.standard));

  if (showTagging) {
    columns.unshift({
      header: () =>
        (items.length ? <input
          type="checkbox"
          checked={selectionMode === 'all' || promptSelectAll}
          onChange={togglePageTracked}
        /> : null),
      value: (item) =>
        !item.standard ? <input
          type="checkbox"
          value={item.id}
          checked={itemIsSelected(item.id)}
          onChange={toggleTrackedItem}
        /> : null
    });

    columns.push({
      header: 'Tags',
      value: (item) => item.standard ? 'n/a' : <TagList tagNames={item.tag_names}/>,
      className: () => 'snippet-tag-column'
    });
  }

  const selectedItems = items.filter(item => itemIsSelected(item.id));
  const itemDescription = `${selectedItems.length} selected ${pluralize(selectedItems.length, 'template')}`;

  return <div className="snippet-list">
    <div className="snippet-list-header row">
      <PageHeader header={`${displayName} Templates`} className="col-12"/>
      <div className="col-12">
        {showTagging && <BulkActions>
          <Edit disabled={isNoneSelected}>
            <BulkTagAction
              itemDescription={itemDescription}
              onSubmit={tags => addTags(selectedItems, tags).then(loadAll)}
              tags={addableTags(tags, selectedItems)}
            />
            <BulkUntagAction
              itemDescription={itemDescription}
              onSubmit={tags => removeTags(selectedItems, tags).then(loadAll)}
              tags={removableTags(tags, selectedItems)}
            />
          </Edit>
        </BulkActions>}
      </div>
    </div>

    <SubPageNavigation links={links}/>
    {children}

    {filterFields.length !== 0 && <Filters
      count={items.length}
      fields={filterFields}
      selected={filters}
      selectCount={selectedItems.length}
      submitFilters={setFilters}
      tags={tags && tags.length > 0}
      itemsName={pluralize(items.length, `${displayName} Template`)}
    />}

    <ManagedResourceList
      displayType={`${displayName} Template`}

      {...collectionButtonsProp}
      columns={columns}

      items={items}
      fetchItems={loadAll}

      itemButtons={[
        ({editItem}) =>
          <a href="#" onClick={editItem}>
            Edit
          </a>,
        ({item, copyItem}) => !item.standard ?
          <span>
            &nbsp;|&nbsp;
            <a href="#" onClick={copyItem}>Copy</a>
          </span> : null,
        ({item, deleteItem}) => item.updated_at
          ? <span>
            &nbsp;|&nbsp;
            <a href="#" onClick={deleteItem}>
              {item.standard ? 'Revert to system default' : 'Delete'}
            </a>
          </span>
          : null
      ]}

      updateItem={item => save(item).then(reloadTags)}
      deleteItem={item => destroy(item).then(reloadTags)}

      editModal={EditModal || SnippetEditor}
      editModalProps={{
        type: type,
        ready: ready,
        ...editModalProps
      }}

      editModel={editModel}
      copyModel={copyModel}
      deleteModal={ConfirmSnippetDelete}

      loadItemForEdit={(item) => {
        if (item.id) {
          load(item.id);
          setEditSnippet(item.id);
        } else {
          setEditSnippet(null);
        }
      }}

      onSort={onSort}
      sort={sort}
    />
  </div>;
}

SnippetList.propTypes = {
  items: PropTypes.array.isRequired,
  columns: PropTypes.array,
  destroy: PropTypes.func.isRequired,
  displayName: PropTypes.string.isRequired,
  editModal: PropTypes.func,
  editModalProps: PropTypes.object,
  load: PropTypes.func.isRequired,
  loadAll: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  showFormatting: PropTypes.bool,
  tags: PropTypes.array,
  links: PropTypes.array,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),

  // withTagging
  addTags: PropTypes.func,
  reloadTags: PropTypes.func,
  removeTags: PropTypes.func,
};

SnippetList.defaultProps = {
  items: [],
  columns: [
    {header: 'Template Name', value: 'name'},
    {
      header: 'Last Edited At ', className: () => 'snippet-edited-at-column',
      value: (item) => item.updated_at ? `${moment(item.updated_at).format(I18n.date.formats.default_moment)}` : 'Never',
      sortable: true,
      sortName: 'updated_at',
    },
    {header: 'Last Edited By', className: () => 'snippet-edited-by-column', value: item => `${item.editor ? item.editor : ''}`}
  ],
  destroy: () => true,
  displayName: '',
  editModal: () => false,
  editModalProps: {},
  filters: {},
  load: () => true,
  loadAll: () => true,
  save: () => true,
  setFilters: () => {},
  tags: null,
  type: '',
  showFormatting: true,
  links: [],
  children: [],

  // withTagging dummy implementations
  addTags: () => {},
  reloadTags: () => {},
  removeTags: () => {},
};

const ConfirmSnippetDelete = ({ item, handleSubmit, onClose }) => {
  return (
    <Modal show className="snippet-editor-modal modal snippet-confirm-delete" backdrop='static' keyboard={false}>
      <Modal.Header>
        <h1 className='modal-title' style={{ display: 'inline-block' }}>Confirm Delete</h1>
        <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
      </Modal.Header>
      <Modal.Body>
        Are you sure you want to&nbsp;
        {
          item.standard
            ? 'revert this template to the system default?'
            : 'delete this template?'
        }
      </Modal.Body>
      <Modal.Footer>
        <Button variant='light' onClick={onClose}>Close</Button>
        <Button variant="danger" type="submit" onClick={(item) => handleSubmit(item)}>Delete</Button>
      </Modal.Footer>
    </Modal>
  )
};

export default SnippetList;
